/**
 * Quick View gallery: slide to variant featured media on swatch change.
 * Gallery is static (not morphed); uses theme xoCarousel API for navigation.
 */
(function () {
  const QUICK_VIEW_SELECTOR = 'xo-product-quick-view';
  const GALLERY_SELECTOR = '.product-info-media-quickview';
  const DESKTOP_BP = 992;

  function getQuickViewRoot(el) {
    return el.closest(QUICK_VIEW_SELECTOR);
  }

  function getGallery(root) {
    return root ? root.querySelector(GALLERY_SELECTOR) : null;
  }

  function getVariantMap(gallery) {
    const mapEl = gallery.querySelector('.product-info-media-quickview__variant-map');
    if (!mapEl) return null;

    try {
      return JSON.parse(mapEl.textContent);
    } catch (_e) {
      return null;
    }
  }

  function getVariantMediaId(input, gallery) {
    const fromInput = input.getAttribute('data-featured-media-id');
    if (fromInput) return String(fromInput);

    const variantId = input.getAttribute('xo-variant-id');
    if (!variantId) return null;

    const map = getVariantMap(gallery);
    if (map && map[variantId]) {
      return String(map[variantId]);
    }

    const root = getQuickViewRoot(input);
    const productEl = root?.querySelector('xo-product[xo-product-id]');
    const dataEl = productEl?.querySelector('xo-product-data');
    if (!dataEl) return null;

    const template = dataEl.querySelector('template');
    const raw = (template?.content?.textContent || dataEl.textContent || '').trim();
    if (!raw) return null;

    try {
      const data = JSON.parse(raw);
      const variant = (data.variants || []).find((v) => String(v.id) === String(variantId));
      if (!variant) return null;

      if (variant.featured_media?.id) {
        return String(variant.featured_media.id);
      }
      if (variant.featured_image?.id) {
        return String(variant.featured_image.id);
      }
    } catch (_e) {
      return null;
    }

    return null;
  }

  function getMainSlides(gallery) {
    const carousel = gallery.querySelector('xo-carousel');
    if (!carousel) return [];

    const mainInner = carousel.querySelector(':scope > xo-carousel-inner');
    if (!mainInner) return [];

    return Array.from(mainInner.querySelectorAll('xo-carousel-slide[data-media-id]'));
  }

  function getCarouselName(gallery) {
    const carousel = gallery.querySelector('xo-carousel');
    if (!carousel) return null;

    return carousel.xoName || carousel.getAttribute('xo-name');
  }

  function goToMediaSlide(gallery, mediaId) {
    if (!mediaId) return false;

    const mainSlides = getMainSlides(gallery);
    const index = mainSlides.findIndex(
      (slide) => String(slide.getAttribute('data-media-id')) === String(mediaId)
    );

    if (index < 0) return false;

    const carouselName = getCarouselName(gallery);
    if (carouselName && window.xoCarousel?.goTo) {
      window.xoCarousel.goTo(carouselName, index);
      return true;
    }

    const thumb = gallery.querySelector(
      `.product-info-media-quickview__thumb-slide[data-media-id="${CSS.escape(String(mediaId))}"]`
    );
    if (thumb) {
      thumb.click();
      return true;
    }

    return false;
  }

  function updateNavVisibility(gallery) {
    const slideCount = Number(gallery.getAttribute('data-qv-slide-count') || 0);
    const count = slideCount || getMainSlides(gallery).length;
    const nav = gallery.querySelector('.product-info-media-quickview__nav');
    if (!nav) return;

    nav.classList.toggle('product-info-media-quickview__nav--single', count <= 1);
  }

  function syncGalleryToVariant(input) {
    const root = getQuickViewRoot(input);
    if (!root) return;

    const gallery = getGallery(root);
    if (!gallery) return;

    updateNavVisibility(gallery);

    const activeInput =
      root.querySelector('xo-product-variants input[type="radio"]:checked') || input;

    const mediaId = getVariantMediaId(activeInput, gallery);
    if (!mediaId) return;

    goToMediaSlide(gallery, mediaId);
  }

  function resolveVariantInput(target) {
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      return target;
    }

    const field = target.closest?.('.xo-field-color');
    if (field) {
      return field.querySelector('input[type="radio"]');
    }

    return null;
  }

  function isQuickViewVariantInput(input) {
    return input?.closest('xo-product-variants, xo-product-quick-view-variant');
  }

  function scheduleVariantSync(input) {
    syncGalleryToVariant(input);
    requestAnimationFrame(() => syncGalleryToVariant(input));
    window.setTimeout(() => syncGalleryToVariant(input), 80);
    window.setTimeout(() => syncGalleryToVariant(input), 400);
  }

  function onVariantInteraction(event) {
    const input = resolveVariantInput(event.target);
    if (!input || !isQuickViewVariantInput(input)) return;
    if (!getQuickViewRoot(input)) return;

    scheduleVariantSync(input);
  }

  const heightObservers = new WeakMap();

  function syncQuickViewHeightsForRoot(root) {
    const media = root.querySelector('.xo-main-product-quickview__media');
    const content = root.querySelector('.xo-main-product-quickview__content');
    if (!content) return;

    if (window.innerWidth < DESKTOP_BP) {
      content.style.minHeight = '';
      return;
    }

    if (media) {
      const mediaH = Math.ceil(media.getBoundingClientRect().height);
      if (mediaH > 0) {
        content.style.minHeight = `${mediaH}px`;
      }
    }
  }

  function syncQuickViewHeights() {
    document.querySelectorAll(QUICK_VIEW_SELECTOR).forEach(syncQuickViewHeightsForRoot);
  }

  function observeQuickViewHeights(root) {
    const media = root.querySelector('.xo-main-product-quickview__media');
    if (!media) return;

    if (!heightObservers.has(root)) {
      const observer = new ResizeObserver(() => {
        requestAnimationFrame(() => syncQuickViewHeightsForRoot(root));
      });
      observer.observe(media);
      heightObservers.set(root, observer);
    }

    root.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', () => syncQuickViewHeightsForRoot(root), { once: true });
      }
    });
  }

  function setupQuickViewRoot(root) {
    const gallery = getGallery(root);
    if (gallery) updateNavVisibility(gallery);
    observeQuickViewHeights(root);
    syncQuickViewHeightsForRoot(root);
  }

  document.addEventListener('change', onVariantInteraction, true);
  document.addEventListener('input', onVariantInteraction, true);

  document.addEventListener(
    'click',
    (event) => {
      const field = event.target.closest?.('.xo-field-color--pill, .xo-field-color');
      if (!field) return;

      const input = field.querySelector('input[type="radio"]');
      if (!input || !isQuickViewVariantInput(input)) return;
      if (!getQuickViewRoot(input)) return;

      scheduleVariantSync(input);
    },
    true
  );

  window.addEventListener('resize', syncQuickViewHeights);

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(QUICK_VIEW_SELECTOR).forEach(setupQuickViewRoot);
  });

  let syncRaf = 0;
  const observer = new MutationObserver(() => {
    cancelAnimationFrame(syncRaf);
    syncRaf = requestAnimationFrame(() => {
      document.querySelectorAll(QUICK_VIEW_SELECTOR).forEach(setupQuickViewRoot);
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
